import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import { useNotifier } from "../../../Core/Notifier";
import {
  useAssignClassesCreateMutation,
  useGetTeamQuery,
  useGetallLocationQuery,
  useGetallSubjectQuery,
  useGetStaffGradesAssignStaffGetPerentQuery,
  useGetAssignClassGetAllCalederQuery,
} from "../../../Api/attoDeskApi";
import {
  CustomerOption,
  IClassCreate,
  IStudentLocation,
  ITeam,
  IGrade,
  ISubject,
  IAssignClassListDetails,
} from "../../../Api/Interface/api.interface";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isAssignmentContext, resolveSafeReturnTo, resolveSafeReturnUrl } from "./assignmentContext";
import {
  rangeHasSelectedWeekday,
  isPeriodRangeSubmittable,
} from "./periodValidation";
import { ClassType, TeachMood } from "../../../Core/Enum/enum";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import dayjs from "dayjs";
import LoadingPage from "../../../Components/LoadingDialog/LoadingPage";
import BackDrop from "../../../Components/Loader/BackDrop";
import ClassForm, {
  requiresLocation,
  TimeConflict,
} from "../ClassForm";
import { usePermission } from "../../../hooks/usePermission";

// Styled components, helpers (studyMoodLabel, modeFromToggles, togglesFromMode,
// requiresLocation), and the full form JSX now live in ../ClassForm — this
// page only wires state, hooks, validation, and submit.

const Index = () => {
  const navigate = useNavigate();
  const lastCustomerId = localStorage.getItem("lastStaffId");

  // ── Assign-Class return context ──
  // When Create Class is opened from a student's Assign Class page it carries
  // source/studentId/returnTo (+ prefill hints). Only then does the form prefill
  // from the student and return to that Assign Class page after a save. Normal
  // creation (from the Classes menu) has no source → unchanged behaviour.
  const [searchParams] = useSearchParams();
  const ctxSource = searchParams.get("source");
  const ctxStudentId = searchParams.get("studentId");
  const isAssignmentSource = isAssignmentContext(ctxSource, ctxStudentId);
  // Validated return target — can only navigate to a student Assign Class route
  // inside this app (never an arbitrary/external URL).
  const safeReturnTo = resolveSafeReturnTo(
    ctxSource,
    ctxStudentId,
    searchParams.get("returnTo")
  );
  const ctxProgramType = searchParams.get("program_type");
  const ctxClassMode = searchParams.get("class_mode");
  const ctxLocationId = searchParams.get("location_id");
  const ctxSubjectSgId = searchParams.get("subject_sg_id");

  // Program type lives on the class (regular | special); defaults to regular.
  const [programType, setProgramType] = useState<string>(
    ctxProgramType === "special" ? "special" : "regular"
  );
  const prefillAppliedRef = useRef(false);
  const subjectPrefillRef = useRef(false);

  const { showErrorMessage, showMessage } = useNotifier();
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(currentSearchQuery, 300);
  const [getStaffID, setGetStaffId] = useState(lastCustomerId || "");

  const [newCompany, { isLoading }] = useAssignClassesCreateMutation();
  const [selectedSubject, setSelectedSubject] = useState<number[]>([]);
  const [selectedWeekDays, setSelectedWeeekDays] = useState<string[]>([]);
  // Global subject creation was removed from this page — subjects can only
  // be COMPOSED into a class from grade+subject rows that already exist.
  // The usePermission import is kept so future permission gates remain
  // one line away.
  usePermission();

  const { data: locationData, isLoading: locationIsLoading } =
    useGetallLocationQuery({ searchText: "" });

  const {
    data: studentPerentData,
    isLoading: studentPerentIsLoading,
    refetch,
  } = useGetTeamQuery({ searchText: currentSearchQuery, id: "" });

  const {
    data: subjectData,
    isLoading: subjectIsLoading,
    refetch: subjectRefetch,
  } = useGetallSubjectQuery({ searchText: "" });

  const {
    data: subjectGradeData,
    isLoading: subjectGradeIsLoading,
    refetch: subjectGradeRefetch,
    isError,
  } = useGetStaffGradesAssignStaffGetPerentQuery({
    id: String(getStaffID),
  });

  // Fetch existing classes for selected staff (for conflict detection)
  const { data: staffScheduleData } = useGetAssignClassGetAllCalederQuery(
    {
      startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
      endDate: dayjs().add(2, "month").endOf("month").format("YYYY-MM-DD"),
      empid: getStaffID || "",
    },
    { skip: !getStaffID }
  );

  const staffExistingClasses = useMemo((): IAssignClassListDetails[] => {
    if (!staffScheduleData?.data || !Array.isArray(staffScheduleData.data)) return [];
    return staffScheduleData.data as IAssignClassListDetails[];
  }, [staffScheduleData]);

  const studentPerentList = useMemo(() => {
    return (studentPerentData?.data ?? []) as ITeam[];
  }, [studentPerentData?.data]);

  const locationList = useMemo(() => {
    return (locationData?.data ?? []) as IStudentLocation[];
  }, [locationData?.data]);

  const subjectDataList: ISubject[] = Array.isArray(subjectData?.data)
    ? (subjectData?.data as ISubject[])
    : [];

  const subjectGRadeDataList: IGrade[] = Array.isArray(subjectGradeData?.data)
    ? (subjectGradeData?.data as IGrade[])
    : [];

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const formik = useFormik<IClassCreate>({
    initialValues: {
      staff_id: lastCustomerId || "",
      location_id: "",
      class_mode: "",
      class_type: "",
      number_of_student: null,
      select_grade_subject_ids: [],
      period_based_from_date: null,
      period_based_to_date: null,
      notes: "",
      selected_days: [],
      startTime: "",
      endTime: "",
      default_fee: null,
      default_tax_status: "exempt",
      default_fee_effective_from: dayjs().format("YYYY-MM-DD"),
    },
    enableReinitialize: true,

    validate: (values) => {
      const errors: { [key: string]: string } = {};
      const today = dayjs().startOf("day");
      const isPeriodBased =
        values.class_type === ClassType["Period-Based"];

      // Period-Based classes are bounded by a date range — both ends are
      // mandatory and a selected weekday must actually fall inside the range.
      if (isPeriodBased && !values.period_based_from_date) {
        errors.period_based_from_date = "Start date is required";
      }
      if (isPeriodBased && !values.period_based_to_date) {
        errors.period_based_to_date = "End date is required";
      }

      if (values.period_based_from_date) {
        const fromDate = values.period_based_from_date.startOf("day");
        if (fromDate.isBefore(today)) {
          errors.period_based_from_date = "From date cannot be in the past";
        }
      }

      if (values.period_based_from_date && values.period_based_to_date) {
        const fromDate = values.period_based_from_date.startOf("day");
        const toDate = values.period_based_to_date.startOf("day");
        if (toDate.isBefore(fromDate)) {
          errors.period_based_to_date =
            "To date must be the same as or after From date";
        } else if (
          isPeriodBased &&
          selectedWeekDays.length > 0 &&
          !rangeHasSelectedWeekday(fromDate, toDate, selectedWeekDays)
        ) {
          errors.period_based_to_date =
            "None of the selected days fall within this date range";
        }
      }

      const isValidTime = (t: string) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(t);

      const toMinutes = (time: string) => {
        const [h, m] = time.split(":").map(Number);
        return h * 60 + m;
      };

      if (!values.startTime || !isValidTime(values.startTime)) {
        errors.startTime = "Start time is required (HH:mm)";
      }

      if (!values.endTime || !isValidTime(values.endTime)) {
        errors.endTime = "End time is required (HH:mm)";
      }

      if (
        values.startTime &&
        values.endTime &&
        isValidTime(values.startTime) &&
        isValidTime(values.endTime)
      ) {
        const startMin = toMinutes(values.startTime);
        const endMin = toMinutes(values.endTime);
        if (endMin <= startMin) {
          errors.endTime = "End time must be after start time";
        }
      }

      return errors;
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          staff_id: values.staff_id,
          location_id: values.location_id,
          class_mode: values.class_mode,
          class_type: values.class_type,
          program_type: programType,
          number_of_student: values.number_of_student,
          select_grade_subject_ids: selectedSubject,
          notes: values.notes,
          selected_days: selectedWeekDays,
          period_based_from_date: values.period_based_from_date
            ? values.period_based_from_date.format("YYYY-MM-DD")
            : null,
          period_based_to_date: values.period_based_to_date
            ? values.period_based_to_date.format("YYYY-MM-DD")
            : null,
          startTime: values.startTime,
          endTime: values.endTime,
          // Billing v2: send fee fields only if the admin actually filled
          // one in — a fee-less class stays fee-less on the server.
          default_fee: values.default_fee ?? null,
          default_tax_status: values.default_tax_status ?? "exempt",
          default_fee_effective_from: values.default_fee_effective_from ?? dayjs().format("YYYY-MM-DD"),
        };

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage("Class created successfully.");
          const newClassId = (response as any)?.data?.class_id;
          const back = safeReturnTo || resolveSafeReturnUrl(searchParams.get("return_url"));

          // Assignment flow has its own round-trip — never divert it. For every
          // other entry point, prefer the Class Detail page over whatever list
          // the user came from: the list refetches the whole visible window
          // (slow for large date ranges), while the detail route fetches ONE
          // record and so the new class is visible immediately. The original
          // caller is still reachable via `return_url` so a "Back to …" link in
          // the detail page can jump straight back.
          if (newClassId && !safeReturnTo) {
            const retSep = back && back !== "/" ? `?return_url=${encodeURIComponent(back)}` : "";
            navigate(`/class-schedules/${newClassId}${retSep}`);
          } else {
            const sep = back.includes("?") ? "&" : "?";
            navigate(newClassId ? `${back}${sep}created_class_id=${newClassId}` : back);
          }
          resetForm();
        }
      } catch (error: any) {
        const apiMsg =
          error?.data?.message || error?.message || "";
        if (apiMsg) {
          showErrorMessage(apiMsg);
        } else {
          showErrorMessage("Something went wrong");
        }
      }
    },
  });

  useEffect(() => {
    if (
      lastCustomerId &&
      studentPerentList.length > 0 &&
      !formik.values.class_mode
    ) {
      const staff = studentPerentList.find(
        (s) => s.id === Number(lastCustomerId)
      );
      if (staff?.mode) {
        const modes = staff.mode.split(",").filter(Boolean);
        if (modes.length === 1) {
          const mapped =
            modes[0] === "InPerson"
              ? TeachMood.Offline
              : modes[0] === "Online"
              ? TeachMood.Online
              : "";
          if (mapped) {
            formik.setFieldValue("class_mode", mapped);
            if (mapped === TeachMood.Online)
              formik.setFieldValue("location_id", "");
          }
        }
      }
    }
  }, [studentPerentList]);

  // Prefill teaching mode + location from the student (assignment source only),
  // once. Runs before the staff effect can set class_mode (which is guarded by
  // `!formik.values.class_mode`), so the student's mode wins.
  useEffect(() => {
    if (prefillAppliedRef.current || !isAssignmentSource) return;
    prefillAppliedRef.current = true;
    if (ctxClassMode === TeachMood.Online || ctxClassMode === TeachMood.Offline) {
      formik.setFieldValue("class_mode", ctxClassMode);
      if (ctxClassMode === TeachMood.Online) {
        formik.setFieldValue("location_id", "");
      } else if (ctxLocationId) {
        formik.setFieldValue("location_id", ctxLocationId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssignmentSource]);

  // Prefill the subject — ONLY the exact (grade+subject) pair passed from the
  // assign page, and only once the selected staff can actually teach it. Never
  // auto-selects extra grades/subjects from the staff capability matrix.
  useEffect(() => {
    if (subjectPrefillRef.current || !isAssignmentSource || !ctxSubjectSgId) return;
    const sgId = Number(ctxSubjectSgId);
    if (Number.isNaN(sgId)) return;
    const teachable = subjectGRadeDataList.some((g: IGrade) =>
      (g.selected_sub_array || []).some(
        (s: any) => s.is_assigned_to_staff && s.id === sgId
      )
    );
    if (teachable) {
      subjectPrefillRef.current = true;
      setSelectedSubject([sgId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectGRadeDataList, isAssignmentSource, ctxSubjectSgId]);

  const handleClassFromChange = (value: string) => {
    formik.setFieldValue("class_mode", value);
    // Location only makes sense for modes that include In-Person. Online-only
    // and empty clear the location; Offline and Hybrid keep whatever's set.
    if (value === TeachMood.Online || value === "") {
      formik.setFieldValue("location_id", "");
    }
  };

  const handleClassTypeFromChange = (value: string) => {
    formik.setFieldValue(
      "class_type",
      formik.values.class_type === value ? "" : value
    );
  };

  const studentPerentOptions = useMemo(() => {
    if (!Array.isArray(studentPerentList)) return [];
    return studentPerentList.map((cust) => ({
      label: `${cust.teamFirstName || ""} ${cust.middle_name || ""} ${
        cust.teamLastName || ""
      }`.trim(),
      value: cust.id,
    }));
  }, [studentPerentList]);

  const locationOptions = useMemo(() => {
    if (!Array.isArray(locationList)) return [];
    return locationList.map((cust) => ({
      label: `${cust.location_name || cust.location_address}`,
      value: cust.id,
    }));
  }, [locationList]);

  // ── Parse a staff member's assigned location IDs (JSON array or scalar) ──
  const parseStaffLocationIds = (raw: any): number[] => {
    if (raw === null || raw === undefined || raw === "") return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(Number).filter(Boolean);
      if (typeof parsed === "number") return [parsed];
    } catch {
      const num = Number(raw);
      if (!isNaN(num) && num > 0) return [num];
    }
    return [];
  };

  // ── Parse selected staff's assigned location IDs ──
  const staffLocationIds = useMemo((): number[] => {
    if (!formik.values.staff_id) return [];
    const staff = studentPerentList.find((s) => s.id === Number(formik.values.staff_id));
    return parseStaffLocationIds(staff?.location_id);
  }, [formik.values.staff_id, studentPerentList]);

  // ── Filter location options based on staff assignment ──
  const filteredLocationOptions = useMemo(() => {
    if (staffLocationIds.length === 0) return locationOptions;
    return locationOptions.filter((opt) => staffLocationIds.includes(opt.value));
  }, [locationOptions, staffLocationIds]);

  // ── Filter staff options by the class's chosen location ──
  // Spec: when a class location is set, only show staff assigned to it. Staff
  // with no location assigned stay visible (fail-open, matches backend scoping).
  const filteredStaffOptions = useMemo(() => {
    const locId = Number(formik.values.location_id);
    if (!locId) return studentPerentOptions;
    return studentPerentOptions.filter((opt) => {
      const staff = studentPerentList.find((s) => s.id === opt.value);
      const ids = parseStaffLocationIds(staff?.location_id);
      return ids.length === 0 || ids.includes(locId);
    });
  }, [studentPerentOptions, studentPerentList, formik.values.location_id]);

  // ── Check if selected location is valid for current staff ──
  const isLocationInvalid = useMemo(() => {
    if (!formik.values.location_id || staffLocationIds.length === 0) return false;
    return !staffLocationIds.includes(Number(formik.values.location_id));
  }, [formik.values.location_id, staffLocationIds]);

  // ── Conflict Detection ──
  const timeToMinutes = (t: string): number => {
    if (!t) return -1;
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  // Detect conflicts as soon as startTime OR endTime is available (don't wait for both)
  const timeConflicts = useMemo(() => {
    if (!formik.values.staff_id || selectedWeekDays.length === 0) return [];
    if (!formik.values.startTime && !formik.values.endTime) return [];

    const newStart = formik.values.startTime ? timeToMinutes(formik.values.startTime) : -1;
    const newEnd = formik.values.endTime ? timeToMinutes(formik.values.endTime) : -1;

    // If only startTime is set, check if it falls within any existing class window
    // If both set, do full overlap check
    const hasStart = newStart >= 0;
    const hasEnd = newEnd >= 0;
    if (!hasStart && !hasEnd) return [];

    const conflicts: Array<{
      day: string;
      startTime: string;
      endTime: string;
      classId: number;
    }> = [];

    staffExistingClasses.forEach((cls) => {
      const sched = cls.schedule;
      if (!sched?.startTime || !sched?.endTime || !sched?.day) return;
      if (sched.status === "deleted") return;
      if (!selectedWeekDays.includes(sched.day)) return;
      // Defense in depth: the calendar endpoint should already scope by
      // empid (fixed in classesAssignController::showScheduleMonthForEmp),
      // but we also drop any row that doesn't belong to the currently
      // selected staff so a stale/legacy backend can never produce false
      // conflicts attributed to this staff.
      const schedStaffId = (sched as any)?.staff_id;
      if (schedStaffId != null && String(schedStaffId) !== String(formik.values.staff_id)) return;

      const existStart = timeToMinutes(sched.startTime);
      const existEnd = timeToMinutes(sched.endTime);
      if (existStart < 0 || existEnd < 0) return;

      let isOverlap = false;

      if (hasStart && hasEnd && newEnd > newStart) {
        // Full overlap check: newStart < existEnd AND newEnd > existStart
        isOverlap = newStart < existEnd && newEnd > existStart;
      } else if (hasStart) {
        // Start-only check: does the start time fall inside an existing class?
        isOverlap = newStart >= existStart && newStart < existEnd;
      }

      if (isOverlap) {
        conflicts.push({
          day: sched.day,
          startTime: sched.startTime,
          endTime: sched.endTime,
          classId: sched.class_id,
        });
      }
    });

    // Deduplicate by classId+day
    const seen = new Set<string>();
    return conflicts.filter((c) => {
      const key = `${c.classId}-${c.day}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [
    formik.values.staff_id,
    formik.values.startTime,
    formik.values.endTime,
    selectedWeekDays,
    staffExistingClasses,
  ]);

  const hasConflicts = timeConflicts.length > 0;

  // Time validity check (start must be before end)
  const timeOrderError = useMemo(() => {
    if (!formik.values.startTime || !formik.values.endTime) return "";
    const s = timeToMinutes(formik.values.startTime);
    const e = timeToMinutes(formik.values.endTime);
    if (s < 0 || e < 0) return "";
    if (e <= s) return "End time must be after start time";
    return "";
  }, [formik.values.startTime, formik.values.endTime]);

  const formValid = useMemo(() => {
    if (!formik.values.staff_id || !formik.values.startTime || !formik.values.endTime) return false;
    if (!formik.values.class_mode) return false;
    if (requiresLocation(formik.values.class_mode) && !formik.values.location_id) return false;
    if (isLocationInvalid) return false;
    if (hasConflicts) return false;
    if (selectedWeekDays.length === 0) return false;
    if (timeOrderError) return false;

    // Period-Based: both dates required, end on/after start, and at least one
    // selected weekday must fall inside the range (else nothing would generate).
    if (
      formik.values.class_type === ClassType["Period-Based"] &&
      !isPeriodRangeSubmittable(
        formik.values.period_based_from_date,
        formik.values.period_based_to_date,
        selectedWeekDays
      )
    ) {
      return false;
    }
    return true;
  }, [formik, isLocationInvalid, hasConflicts, selectedWeekDays, timeOrderError]);

  const subjectMap = useMemo(() => {
    const map = new Map();
    subjectDataList?.forEach((user) => {
      map.set(user.id, `${user.subject_name}`);
    });
    return map;
  }, [subjectDataList]);

  const handleSubjectChange = (categoryId: number) => {
    setSelectedSubject((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleWeekDaysChange = (weekday: string) => {
    setSelectedWeeekDays((prev) =>
      prev.includes(weekday)
        ? prev.filter((day) => day !== weekday)
        : [...prev, weekday]
    );
  };

  const handleStaffChange = (_: any, newValue: CustomerOption | null) => {
    const staffId = newValue?.value ? String(newValue.value) : "";
    formik.setFieldValue("staff_id", staffId);
    setGetStaffId(staffId);
    setSelectedSubject([]);
    setSelectedWeeekDays([]);

    if (staffId) {
      const staff = studentPerentList.find((s) => s.id === Number(staffId));

      // Auto-set teaching mode
      if (staff?.mode) {
        const modes = staff.mode.split(",").filter(Boolean);
        if (modes.length === 1) {
          const mapped =
            modes[0] === "InPerson"
              ? TeachMood.Offline
              : modes[0] === "Online"
              ? TeachMood.Online
              : "";
          if (mapped) {
            formik.setFieldValue("class_mode", mapped);
            if (mapped === TeachMood.Online)
              formik.setFieldValue("location_id", "");
          }
        } else {
          formik.setFieldValue("class_mode", "");
        }
      } else {
        formik.setFieldValue("class_mode", "");
      }

      // Clear location if staff doesn't have current location assigned
      if (staff?.location_id && formik.values.location_id) {
        let staffLocs: number[] = [];
        try {
          const parsed = JSON.parse(staff.location_id);
          staffLocs = Array.isArray(parsed) ? parsed.map(Number) : [Number(parsed)];
        } catch {
          const n = Number(staff.location_id);
          if (!isNaN(n) && n > 0) staffLocs = [n];
        }
        if (staffLocs.length > 0 && !staffLocs.includes(Number(formik.values.location_id))) {
          formik.setFieldValue("location_id", "");
        }
      }

      // Auto-select location if staff has exactly one
      if (staff?.location_id) {
        let staffLocs: number[] = [];
        try {
          const parsed = JSON.parse(staff.location_id);
          staffLocs = Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : [];
          if (typeof parsed === "number") staffLocs = [parsed];
        } catch {
          const n = Number(staff.location_id);
          if (!isNaN(n) && n > 0) staffLocs = [n];
        }
        if (staffLocs.length === 1) {
          formik.setFieldValue("location_id", String(staffLocs[0]));
        }
      }
    } else {
      formik.setFieldValue("class_mode", "");
      formik.setFieldValue("location_id", "");
    }
  };

  const pageIsLoading =
    locationIsLoading ||
    studentPerentIsLoading ||
    subjectIsLoading ||
    subjectGradeIsLoading;

  if (pageIsLoading) {
    return <LoadingPage open={pageIsLoading} />;
  }

  // When the ClassForm's "Add Teaching Subject" dialog returns newly-picked
  // grade+subject pair-ids, append them to the local selectedSubject array.
  // Nothing else about the form is touched — staff, schedule, mode,
  // capacity, program, location, days, times, recurrence all persist.
  const handleAddSubjectAssignments = (newIds: number[]) => {
    if (!newIds || newIds.length === 0) return;
    setSelectedSubject((prev) => {
      const set = new Set(prev);
      newIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  return (
    <Box sx={{ pb: 4 }}>
      <ClassForm
        mode="create"
        formik={formik}
        formValid={formValid}
        submitting={isLoading}
        studentPerentOptions={studentPerentOptions}
        filteredStaffOptions={filteredStaffOptions}
        studentPerentIsLoading={studentPerentIsLoading}
        onStaffChange={handleStaffChange}
        onStaffSearchInput={(v) => setCurrentSearchQuery(v)}
        locationOptions={locationOptions}
        filteredLocationOptions={filteredLocationOptions}
        staffLocationIds={staffLocationIds}
        isLocationInvalid={isLocationInvalid}
        locationIsLoading={locationIsLoading}
        onClassModeChange={handleClassFromChange}
        onClassTypeChange={handleClassTypeFromChange}
        programType={programType as "regular" | "special"}
        onProgramTypeChange={(t) => setProgramType(t)}
        subjectGRadeDataList={subjectGRadeDataList}
        subjectMap={subjectMap}
        subjectIsLoading={subjectIsLoading}
        subjectGradeIsLoading={subjectGradeIsLoading}
        isSubjectError={!!isError}
        selectedSubject={selectedSubject}
        onSubjectToggle={handleSubjectChange}
        onAddSubjectAssignments={handleAddSubjectAssignments}
        selectedWeekDays={selectedWeekDays}
        onWeekDayToggle={handleWeekDaysChange}
        timeConflicts={timeConflicts as TimeConflict[]}
        hasConflicts={hasConflicts}
        timeOrderError={timeOrderError}
        submitLabel="Create Class"
        onReset={() => {
          formik.resetForm();
          setSelectedSubject([]);
          setSelectedWeeekDays([]);
        }}
        onCancel={isAssignmentSource ? () => navigate(safeReturnTo) : undefined}
        showCancel={isAssignmentSource}
      />
      {isLoading && <BackDrop open={isLoading} />}
    </Box>
  );
};

export default Index;
